import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react"
import { toast } from "react-toastify"
import authService from "../services/authService"
import { SOCKET_URL } from "../utils/constants"
import io from "socket.io-client"
import { useAuth } from "../lib/useAuth"

// Create socket context
const SocketContext = createContext(null)

// Socket event types
export const SOCKET_EVENTS = {
    // Connection events
    CONNECT: "connect",
    DISCONNECT: "disconnect",
    CONNECT_ERROR: "connect_error",
    RECONNECT: "reconnect",
    RECONNECT_ATTEMPT: "reconnect_attempt",
    RECONNECT_ERROR: "reconnect_error",

    // Server events
    SERVER_STATUS: "server_status",
    SERVER_UPDATE: "server_update",
    SERVER_START: "server_start",
    SERVER_STOP: "server_stop",
    SERVER_RESTART: "server_restart",

    // Bot events
    BOT_STATUS: "bot_status",
    BOT_UPDATE: "bot_update",
    BOT_CONNECT: "bot_connect",
    BOT_DISCONNECT: "bot_disconnect",

    // User events
    USER_UPDATE: "user_update",
    USER_BALANCE: "user_balance",
    USER_NOTIFICATION: "user_notification",

    // System events
    SYSTEM_ALERT: "system_alert",
    MAINTENANCE: "maintenance",
    UPDATE_AVAILABLE: "update_available",

    // Custom events
    CUSTOM_MESSAGE: "custom_message",
    DATA_SYNC: "data_sync",
}

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null)
    const [isConnected, setIsConnected] = useState(false)
    const [connectionStatus, setConnectionStatus] = useState("disconnected")
    const [lastMessage, setLastMessage] = useState(null)
    const [messageHistory, setMessageHistory] = useState([])
    const reconnectAttempts = useRef(0)
    const maxReconnectAttempts = 5
    const reconnectDelay = 1000

    // Initialize socket connection
    const initializeSocket = useCallback(() => {
        const token = authService.getToken()

        if (!token || !authService.isTokenValid()) {
            console.log("No valid token available for socket connection")
            return null
        }

        console.log("Initializing Socket.IO connection to:", SOCKET_URL)

        // Create Socket.IO client instance
        const socketIO = io(SOCKET_URL, {
            reconnection: true,
            reconnectionAttempts: 50,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000,
            timeout: 20000,
            extraHeaders: {
                Authorization: "Bearer " + token,
            },
        })

        // Set up event handlers
        socketIO.on("connect", () => {
            console.log("Socket.IO connected")
            setIsConnected(true)
            setConnectionStatus("connected")
            reconnectAttempts.current = 0
            setSocket(socketIO)
        })

        socketIO.on("disconnect", (reason) => {
            console.log("Socket.IO disconnected:", reason)
            setIsConnected(false)
            setConnectionStatus("disconnected")
            setSocket(null)
        })

        socketIO.on("connect_error", (error) => {
            console.error("Socket.IO connection error:", error)
            setConnectionStatus("error")
            toast.error("اتصال به سرور برقرار نشد")
        })

        socketIO.on("reconnect", (attempt) => {
            console.log("Socket.IO reconnected after", attempt, "attempts")
            setIsConnected(true)
            setConnectionStatus("connected")
            reconnectAttempts.current = 0
        })

        socketIO.on("reconnect_attempt", (attempt) => {
            console.log("Socket.IO reconnect attempt:", attempt)
            reconnectAttempts.current = attempt
            setConnectionStatus(`reconnecting (${attempt}/${maxReconnectAttempts})`)
        })

        socketIO.on("reconnect_error", (error) => {
            console.error("Socket.IO reconnect error:", error)
        })

        // Handle custom events
        socketIO.on("auth_success", (data) => {
            console.log("Socket authentication successful", data)
            handleSocketMessage({ type: "auth_success", payload: data })
        })

        socketIO.on("auth_error", (data) => {
            console.error("Socket authentication failed:", data)
            handleSocketMessage({ type: "auth_error", payload: data })
        })

        socketIO.on("error", (data) => {
            handleSocketMessage({ type: "error", payload: data })
        })

        return socketIO
    }, [])

    // Handle incoming socket messages
    const handleSocketMessage = useCallback((data) => {
        const { type, payload } = data

        switch (type) {
            case "auth_success":
                console.log("Socket authentication successful")
                toast.success("اتصال با موفقیت برقرار شد")
                break

            case "auth_error":
                console.error("Socket authentication failed:", payload?.message)
                toast.error("خطا در احراز هویت اتصال")
                break

            case "error":
                // Handle errors
                toast.error(payload?.message || "خطا در اتصال")
                break

            default:
                console.log("Unhandled socket message type:", type)
        }
    }, [])

    // Send message through socket
    const sendMessage = useCallback(
        (eventType, payload) => {
            if (socket && isConnected) {
                socket.emit(eventType, payload)
                return true
            } else {
                console.warn("Socket not connected, cannot send message")
                return false
            }
        },
        [socket, isConnected],
    )

    // Subscribe to specific events
    const subscribe = useCallback(
        (eventType, callback) => {
            if (socket && isConnected) {
                socket.on(eventType, callback)
                console.log(`Subscribed to ${eventType}`)
                return true
            }
            return false
        },
        [socket, isConnected],
    )

    // Unsubscribe from specific events
    const unsubscribe = useCallback(
        (eventType, callback) => {
            if (socket) {
                socket.off(eventType, callback)
                console.log(`Unsubscribed from ${eventType}`)
                return true
            }
            return false
        },
        [socket],
    )

    // Initialize socket when component mounts
    useEffect(() => {
        const socketIO = initializeSocket()

        return () => {
            if (socketIO) {
                socketIO.disconnect()
            }
        }
    }, [initializeSocket])

    // Context value
    const contextValue = {
        socket,
        isConnected,
        connectionStatus,
        lastMessage,
        initializeSocket,
        messageHistory,
        sendMessage,
        subscribe,
        unsubscribe,
        reconnectAttempts: reconnectAttempts.current,
    }

    return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>
}

// Custom hook to use socket
export const useSocket = () => {
    const context = useContext(SocketContext)
    if (context === undefined) {
        throw new Error("useSocket must be used within a SocketProvider")
    }
    return context
}

// Hook for specific socket events
export const useSocketEvent = (eventType, callback) => {
    const { socket, isConnected } = useSocket()

    useEffect(() => {
        if (socket && isConnected && callback) {
            // Subscribe directly to avoid dependency issues
            socket.on(eventType, callback)
            console.log(`Subscribed to ${eventType}`)
        }

        return () => {
            if (socket && callback) {
                socket.off(eventType, callback)
                console.log(`Unsubscribed from ${eventType}`)
            }
        }
    }, [socket, isConnected, eventType, callback])

    return { isConnected }
}
